// File: PageBuilding/WidgetRenderer.tsx
import { Box } from "@mui/material";
import parse from "html-react-parser";
import { useRef, useState } from "react";
import {
  needsIframeRendering,
  rewriteRelativeUrls,
} from "../PageBuilderHelpers/helpers";

// ---------------------------------------------------------------------------
// WidgetIframeRenderer
// ---------------------------------------------------------------------------

/**
 * Renders a widget inside an isolated srcdoc iframe so that its JavaScript
 * initialises correctly (carousels, sliders, etc.).
 * Only used when needsIframeRendering() returns true.
 */
export const WidgetIframeRenderer = ({
  html,
  siteBaseUrl,
}: {
  html: string;
  siteBaseUrl?: string;
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(300);

  const processedHtml = siteBaseUrl
    ? rewriteRelativeUrls(html, siteBaseUrl)
    : html;

  const baseTag = siteBaseUrl
    ? `<base href="${siteBaseUrl}/" target="_blank">`
    : "";

  const srcdoc = `<!DOCTYPE html><html><head>${baseTag}<style>html,body{margin:0;padding:0;}</style></head><body>${processedHtml}</body></html>`;

  const handleLoad = () => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc) {
        const scrollH =
          doc.documentElement.scrollHeight || doc.body.scrollHeight;
        if (scrollH > 0) setHeight(scrollH);
      }
    } catch {
      // sandboxed cross-origin frame — keep default height
    }
  };

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcdoc}
      title="Widget Preview"
      sandbox="allow-scripts allow-same-origin allow-popups"
      style={{ width: "100%", height: `${height}px`, border: "none", display: "block" }}
      onLoad={handleLoad}
    />
  );
};

// ---------------------------------------------------------------------------
// WidgetContent
// ---------------------------------------------------------------------------

/**
 * Entry point for desktop widget content rendering.
 * Delegates to WidgetIframeRenderer for JS-driven widgets (carousel etc.),
 * and falls back to the original parse() behaviour for everything else
 * so existing widgets have zero visual change.
 */
export const WidgetContent = ({
  html,
  siteBaseUrl,
}: {
  html: string;
  siteBaseUrl?: string;
}) => {
  if (needsIframeRendering(html)) {
    return <WidgetIframeRenderer html={html} siteBaseUrl={siteBaseUrl} />;
  }
  return <Box>{parse(html)}</Box>;
};
